import * as THREE from "three";

export interface SceneSetupResult {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  anatomyGroup: THREE.Group;
  restorationGroup: THREE.Group;
}

export interface JawRigResult {
  upperJawPivotGroup: THREE.Group;
  upperJawContentGroup: THREE.Group;
  lowerJawPivotGroup: THREE.Group;
  lowerJawContentGroup: THREE.Group;
}

export function createViewportScene(
  host: HTMLDivElement
): SceneSetupResult {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf8fafc);

  const width = host.clientWidth || 600;
  const height = host.clientHeight || 400;

const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 2000);
camera.up.set(0, 1, 0);
camera.position.set(0, 90, 180);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(width, height);
  host.appendChild(renderer.domElement);

  const ambient = new THREE.AmbientLight(0xffffff, 1.5);
  scene.add(ambient);

  const directional = new THREE.DirectionalLight(0xffffff, 2);
  directional.position.set(50, 100, 80);
  scene.add(directional);

  const grid = new THREE.GridHelper(220, 22);
  scene.add(grid);

  const axes = new THREE.AxesHelper(40);
  scene.add(axes);

  const anatomyGroup = new THREE.Group();
  anatomyGroup.name = "anatomy-group";
  scene.add(anatomyGroup);

  const restorationGroup = new THREE.Group();
  restorationGroup.name = "restoration-group";
  scene.add(restorationGroup);

  return {
    scene,
    camera,
    renderer,
    anatomyGroup,
    restorationGroup,
  };
}

export function createJawRig(parent: THREE.Group): JawRigResult {
  const upperJawPivotGroup = new THREE.Group();
  upperJawPivotGroup.name = "upper-jaw-pivot";

  const upperJawContentGroup = new THREE.Group();
  upperJawContentGroup.name = "upper-jaw-content";

  const lowerJawPivotGroup = new THREE.Group();
  lowerJawPivotGroup.name = "lower-jaw-pivot";

  const lowerJawContentGroup = new THREE.Group();
  lowerJawContentGroup.name = "lower-jaw-content";

  upperJawPivotGroup.add(upperJawContentGroup);
  lowerJawPivotGroup.add(lowerJawContentGroup);

  parent.add(upperJawPivotGroup);
  parent.add(lowerJawPivotGroup);

  return {
    upperJawPivotGroup,
    upperJawContentGroup,
    lowerJawPivotGroup,
    lowerJawContentGroup,
  };
}

export function createDebugLine(
  start: THREE.Vector3,
  end: THREE.Vector3,
  color: number
): THREE.Line {
  const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
  const material = new THREE.LineBasicMaterial({ color });
  const line = new THREE.Line(geometry, material);
  line.name = "debug-line";
  return line;
}

export function createDebugSphere(
  position: THREE.Vector3,
  color: number,
  radius = 2
): THREE.Mesh {
  const geometry = new THREE.SphereGeometry(radius, 16, 16);
  const material = new THREE.MeshBasicMaterial({ color });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(position);
  mesh.name = "debug-sphere";
  return mesh;
}

export function disposeScene(scene: THREE.Scene): void {
  scene.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }

    const material = mesh.material;
    if (Array.isArray(material)) {
      material.forEach((m) => m.dispose());
    } else if (material) {
      material.dispose();
    }
  });
}